"use client";
import { useEffect } from "react";
import { useRegisterStore } from "cy-remittance/registration";
import {
  applyTokenPrecedence,
  checkSystemHealth,
  LogLevel,
  logReferralEvent,
  safeExecute,
} from "./errorHandling";
import { getReferralCode, setReferralCode } from "./referralStorage";
import { extractAffiliateToken } from "./urlParams";

/**
 * RegistrationInitializer handles URL parameter detection and referral code initialization
 * on page load. This component should be rendered on the registration page to ensure
 * affiliate tokens are properly captured and stored.
 */
export function RegistrationInitializer() {
  const setReferralCodeInState = useRegisterStore(
    (state) => state.setReferralCode,
  );

  useEffect(() => {
    // Only run on client-side
    if (typeof window === "undefined") {
      logReferralEvent(
        LogLevel.WARN,
        "Attempted initialization on server-side",
        {
          component: "RegistrationInitializer",
          action: "initialize",
        },
      );
      return;
    }

    // Check system health before proceeding
    const health = checkSystemHealth();
    if (!health.overallHealthy) {
      logReferralEvent(
        LogLevel.WARN,
        "System unhealthy - proceeding with limited functionality",
        {
          component: "RegistrationInitializer",
          action: "initialize",
          additionalData: health as any,
        },
      );
    }

    safeExecute(
      () => {
        const currentUrl = window.location.href;

        // Extract affiliate token from current URL
        const urlToken = extractAffiliateToken(currentUrl);

        // Get stored token (may be null if storage unavailable)
        const storedToken = getReferralCode();

        // Apply precedence rules to determine which token to use
        const finalToken = applyTokenPrecedence({
          urlToken,
          storedToken,
        });

        if (finalToken) {
          // Store token if it came from URL (for session persistence)
          if (urlToken && urlToken === finalToken) {
            setReferralCode(finalToken);
          }

          // Update registration state
          setReferralCodeInState(finalToken);

          logReferralEvent(
            LogLevel.INFO,
            "Referral code initialized successfully",
            {
              component: "RegistrationInitializer",
              action: "initialize",
              token: finalToken,
              additionalData: {
                source: urlToken ? "url" : "storage",
                healthStatus: health.overallHealthy ? "healthy" : "degraded",
              },
            },
          );
        } else {
          logReferralEvent(
            LogLevel.INFO,
            "No referral code found - proceeding without",
            {
              component: "RegistrationInitializer",
              action: "initialize",
              additionalData: {
                healthStatus: health.overallHealthy ? "healthy" : "degraded",
              },
            },
          );
        }
      },
      {
        component: "RegistrationInitializer",
        action: "initialize",
        url: typeof window !== "undefined" ? window.location.href : undefined,
      },
    );
  }, [setReferralCodeInState]); // Include setReferralCodeInState in dependencies

  // This component doesn't render anything visible
  return null;
}
